import { apiService } from './api';
import { SageEntityLinkDto } from '../types/sage';

/**
 * Reads the HTTP status off either error shape:
 *   - raw AxiosError (`err.response.status`)
 *   - post-PR2 normalized envelope error (`err.httpStatus`)
 */
const statusOf = (err: unknown): number | undefined => {
  if (typeof err !== 'object' || err === null) return undefined;
  const e = err as { response?: { status?: number }; httpStatus?: number };
  return e.response?.status ?? e.httpStatus;
};

/**
 * Service for the Sage entity-link lifecycle: reading link + sync status,
 * creating (ensuring) a Sage customer, and triggering an on-demand sync.
 * The company id is resolved server-side from config — no query params needed.
 */
export const sageEntityLinkService = {
  /**
   * Fetch the entity's active Sage link + sync telemetry.
   * @returns The link, or null when there is no active link (404).
   * @throws Rethrows any non-404 error so callers can surface a real error.
   */
  getLinkStatus: async (
    entityType: string,
    entityId: number,
  ): Promise<SageEntityLinkDto | null> => {
    try {
      const response = await apiService.get<SageEntityLinkDto>(
        `/sage-entity-links/${entityType}/${entityId}`,
      );
      return response.data ?? null;
    } catch (err) {
      if (statusOf(err) === 404) return null;
      throw err;
    }
  },

  /**
   * Ensure a Sage customer exists for the entity (creates it if missing, links it).
   * Lets a 409 SAGE_CUSTOMER_EXISTS conflict propagate so the caller can offer to
   * link the existing customer instead.
   */
  ensureCustomer: async (
    entityType: string,
    entityId: number,
  ): Promise<{ sageCustomerId: string }> => {
    const response = await apiService.post<{ sageCustomerId: string }>(
      `/sage-entity-links/${entityType}/${entityId}/ensure-customer`,
    );
    return response.data ?? { sageCustomerId: '' };
  },

  /**
   * Trigger an on-demand sync between DRIS and the linked Sage record.
   */
  syncNow: async (entityType: string, entityId: number): Promise<void> => {
    await apiService.post(`/sage-entity-links/${entityType}/${entityId}/sync`);
  },
};
